import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Timer, Play, Pause } from 'lucide-react';

interface WorkoutTimerProps {
  isActive: boolean;
  startTime?: Date;
}

export const WorkoutTimer = ({ isActive, startTime }: WorkoutTimerProps) => {
  const [elapsedTime, setElapsedTime] = useState(0);
  const [isRunning, setIsRunning] = useState(isActive);

  useEffect(() => {
    if (!isActive || !startTime) {
      setElapsedTime(0);
      return;
    }

    // Calculate initial elapsed time based on stored start time
    const now = new Date();
    const initialElapsed = Math.floor((now.getTime() - startTime.getTime()) / 1000);
    setElapsedTime(initialElapsed);
    setIsRunning(true);

    // Update timer every second
    const interval = setInterval(() => {
      const currentTime = new Date();
      const newElapsed = Math.floor((currentTime.getTime() - startTime.getTime()) / 1000);
      setElapsedTime(newElapsed);
    }, 1000);

    return () => clearInterval(interval);
  }, [isActive, startTime]);

  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  if (!isActive) {
    return null;
  }

  return (
    <Card className="bg-gradient-to-r from-primary/10 to-primary/5 border-primary/20">
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-primary/10 rounded-full">
              <Timer className="h-4 w-4 text-primary" />
            </div>
            <div>
              <p className="text-sm font-medium">Workout Time</p>
              <p className="text-xs text-muted-foreground">Started at {startTime?.toLocaleTimeString()}</p>
            </div>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold text-primary">{formatTime(elapsedTime)}</div>
            <Badge variant="outline" className="text-xs">
              {isRunning ? (
                <>
                  <Play className="h-3 w-3 mr-1" />
                  Active
                </>
              ) : (
                <>
                  <Pause className="h-3 w-3 mr-1" />
                  Paused
                </>
              )}
            </Badge>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};